import { useState, useEffect, useMemo } from 'react';
import { cariDB, type CariTransaction } from '../../../lib/cariDB';
import { pricingDB } from '../../../lib/pricingDB';

type ClinicRow = {
  clinic_name: string;
  borc: number;
  odeme: number;
  bakiye: number;
  txCount: number;
};

type MonthRow = {
  monthKey: string;
  monthLabel: string;
  borc: number;
  odeme: number;
  bakiye: number;
};

export default function FinancialTab() {
  const [transactions, setTransactions] = useState<CariTransaction[]>([]);
  const [allClinics, setAllClinics] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [fromMonth, setFromMonth] = useState<string>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 5);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [toMonth, setToMonth] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedClinics, setSelectedClinics] = useState<string[]>([]);
  const [clinicDropOpen, setClinicDropOpen] = useState(false);

  const pricing = pricingDB.get();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [txs, clinicsInfo] = await Promise.all([
          cariDB.getAll(),
          cariDB.getAllClinicsForSettings(),
        ]);
        setTransactions(txs);
        const names = clinicsInfo.map((c) => c.clinic_name);
        setAllClinics(names);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Filter transactions by month range + selected clinics
  const filtered = useMemo(() => {
    return transactions.filter((tx) => {
      const txMonth = tx.date.slice(0, 7); // "YYYY-MM"
      if (txMonth < fromMonth || txMonth > toMonth) return false;
      if (selectedClinics.length > 0 && !selectedClinics.includes(tx.clinic_name)) return false;
      return true;
    });
  }, [transactions, fromMonth, toMonth, selectedClinics]);

  // Summary
  const summary = useMemo(() => {
    const borc = filtered.filter((t) => t.type === 'borc').reduce((s, t) => s + t.amount, 0);
    const odeme = filtered.filter((t) => t.type === 'odeme').reduce((s, t) => s + t.amount, 0);
    return { borc, odeme, bakiye: borc - odeme, count: filtered.length };
  }, [filtered]);

  // By-Clinic breakdown
  const clinicRows = useMemo<ClinicRow[]>(() => {
    const map = new Map<string, ClinicRow>();
    filtered.forEach((tx) => {
      if (!map.has(tx.clinic_name)) {
        map.set(tx.clinic_name, { clinic_name: tx.clinic_name, borc: 0, odeme: 0, bakiye: 0, txCount: 0 });
      }
      const row = map.get(tx.clinic_name)!;
      if (tx.type === 'borc') row.borc += tx.amount;
      else row.odeme += tx.amount;
      row.bakiye = row.borc - row.odeme;
      row.txCount++;
    });
    return Array.from(map.values()).sort((a, b) => b.borc - a.borc);
  }, [filtered]);

  // Monthly breakdown
  const monthRows = useMemo<MonthRow[]>(() => {
    const map = new Map<string, MonthRow>();
    filtered.forEach((tx) => {
      const key = tx.date.slice(0, 7);
      if (!map.has(key)) {
        const [yr, mo] = key.split('-');
        const label = new Date(parseInt(yr), parseInt(mo) - 1).toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
        map.set(key, { monthKey: key, monthLabel: label, borc: 0, odeme: 0, bakiye: 0 });
      }
      const row = map.get(key)!;
      if (tx.type === 'borc') row.borc += tx.amount;
      else row.odeme += tx.amount;
      row.bakiye = row.borc - row.odeme;
    });
    return Array.from(map.values()).sort((a, b) => b.monthKey.localeCompare(a.monthKey));
  }, [filtered]);

  const fmt = (n: number) => `${pricing.currency}${n.toLocaleString('tr-TR')}`;

  const toggleClinic = (name: string) => {
    setSelectedClinics((prev) =>
      prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-3 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* ── FILTERS ── */}
      <div className="bg-white rounded-2xl p-5">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-2">
          <i className="ri-filter-3-line text-teal-500 text-base"></i>
          Filtreler
        </p>
        <div className="flex flex-wrap gap-4 items-end">
          {/* From month */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Başlangıç Ayı</label>
            <input
              type="month"
              value={fromMonth}
              onChange={(e) => setFromMonth(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all cursor-pointer"
            />
          </div>
          {/* To month */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Bitiş Ayı</label>
            <input
              type="month"
              value={toMonth}
              onChange={(e) => setToMonth(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all cursor-pointer"
            />
          </div>

          {/* Clinic multi-select */}
          <div className="relative flex-1 min-w-[220px]">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Klinikler</label>
            <button
              type="button"
              onClick={() => setClinicDropOpen((p) => !p)}
              className={`w-full flex items-center justify-between px-3 py-2 border rounded-lg text-sm transition-all cursor-pointer ${
                selectedClinics.length > 0 ? 'border-teal-300 bg-teal-50' : 'border-gray-200 bg-white hover:border-teal-300'
              }`}
            >
              <span className="text-gray-700 truncate">
                {selectedClinics.length === 0
                  ? 'Tüm klinikler'
                  : selectedClinics.length === 1
                  ? selectedClinics[0]
                  : `${selectedClinics.length} klinik seçili`}
              </span>
              <i className={`ri-arrow-down-s-line text-gray-400 ml-2 transition-transform flex-shrink-0 ${clinicDropOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {clinicDropOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl z-30 overflow-hidden shadow-lg">
                <div className="p-2 border-b border-gray-100 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setSelectedClinics(allClinics)}
                    className="text-xs text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
                  >Tümünü Seç</button>
                  <button
                    type="button"
                    onClick={() => setSelectedClinics([])}
                    className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap"
                  >Temizle</button>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {allClinics.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">Klinik bulunamadı</p>
                  ) : (
                    allClinics.map((clinic) => {
                      const isSelected = selectedClinics.includes(clinic);
                      return (
                        <button
                          key={clinic}
                          type="button"
                          onClick={() => toggleClinic(clinic)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm hover:bg-teal-50 cursor-pointer text-left transition-colors ${isSelected ? 'bg-teal-50' : ''}`}
                        >
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-teal-500 border-teal-500' : 'border-gray-300'}`}>
                            {isSelected && <i className="ri-check-line text-white" style={{ fontSize: '10px' }}></i>}
                          </div>
                          <div className="w-6 h-6 rounded-md bg-teal-100 text-teal-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                            {clinic.slice(0, 1).toUpperCase()}
                          </div>
                          <span className="truncate text-gray-700">{clinic}</span>
                        </button>
                      );
                    })
                  )}
                </div>
                <div className="p-2 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setClinicDropOpen(false)}
                    className="w-full py-1.5 text-xs text-gray-500 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
                  >Kapat</button>
                </div>
              </div>
            )}
          </div>

          {/* Reset */}
          {(selectedClinics.length > 0) && (
            <button
              type="button"
              onClick={() => setSelectedClinics([])}
              className="px-3 py-2 text-xs text-red-500 hover:bg-red-50 border border-red-200 rounded-lg transition-all cursor-pointer whitespace-nowrap flex items-center gap-1 font-medium"
            >
              <i className="ri-filter-off-line"></i>Filtreyi Temizle
            </button>
          )}
        </div>
      </div>

      {/* ── SUMMARY CARDS ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Toplam Kazanç', value: summary.borc, icon: 'ri-arrow-up-circle-line', color: 'emerald', textColor: 'text-emerald-600' },
          { label: 'Tahsilat', value: summary.odeme, icon: 'ri-arrow-down-circle-line', color: 'teal', textColor: 'text-teal-600' },
          { label: 'Kalan Bakiye', value: summary.bakiye, icon: 'ri-bank-line', color: summary.bakiye >= 0 ? 'amber' : 'red', textColor: summary.bakiye >= 0 ? 'text-amber-600' : 'text-red-600' },
          { label: 'İşlem Sayısı', value: summary.count, icon: 'ri-receipt-line', color: 'gray', textColor: 'text-gray-700', noFormat: true },
        ].map((card) => (
          <div key={card.label} className="bg-white rounded-2xl p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-gray-500">{card.label}</p>
              <div className={`w-9 h-9 bg-${card.color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
                <i className={`${card.icon} text-${card.color}-600 text-lg`}></i>
              </div>
            </div>
            <p className={`text-2xl font-bold ${card.textColor}`}>
              {card.noFormat ? card.value : fmt(card.value as number)}
            </p>
          </div>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl p-14 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-money-dollar-circle-line text-3xl text-gray-400"></i>
          </div>
          <h3 className="text-base font-semibold text-gray-700 mb-1">Seçilen aralıkta işlem bulunamadı</h3>
          <p className="text-sm text-gray-400">Tarih aralığını veya klinik seçimini değiştirin</p>
        </div>
      ) : (
        <>
          {/* ── BY-CLINIC TABLE ── */}
          <div className="bg-white rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <i className="ri-hospital-line text-teal-500"></i>
                Klinik Bazında Kazanç
              </h3>
              <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">{clinicRows.length} klinik</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-teal-50">
                    <th className="text-left px-6 py-3 font-semibold text-teal-800">Klinik</th>
                    <th className="text-right px-6 py-3 font-semibold text-teal-800">Kazanç</th>
                    <th className="text-right px-6 py-3 font-semibold text-teal-800">Tahsilat</th>
                    <th className="text-right px-6 py-3 font-semibold text-teal-800">Kalan</th>
                    <th className="px-6 py-3 font-semibold text-teal-800 text-center">Tahsilat %</th>
                    <th className="px-6 py-3 font-semibold text-teal-800 min-w-[140px]">Dağılım</th>
                  </tr>
                </thead>
                <tbody>
                  {clinicRows.map((row, i) => {
                    const pct = row.borc > 0 ? (row.odeme / row.borc) * 100 : 0;
                    const sharePct = summary.borc > 0 ? (row.borc / summary.borc) * 100 : 0;
                    return (
                      <tr key={row.clinic_name} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/50'}`}>
                        <td className="px-6 py-3.5">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-lg bg-teal-100 text-teal-700 font-bold text-xs flex items-center justify-center flex-shrink-0">
                              {row.clinic_name.slice(0, 1).toUpperCase()}
                            </div>
                            <span className="font-semibold text-gray-800">{row.clinic_name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-3.5 text-right font-bold text-emerald-600">{fmt(row.borc)}</td>
                        <td className="px-6 py-3.5 text-right text-teal-600 font-medium">{fmt(row.odeme)}</td>
                        <td className="px-6 py-3.5 text-right">
                          <span className={`font-bold ${row.bakiye > 0 ? 'text-amber-600' : row.bakiye === 0 ? 'text-gray-500' : 'text-red-500'}`}>
                            {fmt(row.bakiye)}
                          </span>
                        </td>
                        <td className="px-6 py-3.5 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            pct >= 90 ? 'bg-green-100 text-green-700'
                            : pct >= 50 ? 'bg-amber-100 text-amber-700'
                            : 'bg-red-100 text-red-700'
                          }`}>
                            {pct.toFixed(0)}%
                          </span>
                        </td>
                        <td className="px-6 py-3.5">
                          <div className="w-full bg-gray-100 rounded-full h-1.5">
                            <div className="bg-teal-400 h-1.5 rounded-full" style={{ width: `${sharePct}%` }}></div>
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5">{sharePct.toFixed(0)}% pay</p>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-emerald-50 border-t-2 border-emerald-200">
                    <td className="px-6 py-3.5 font-bold text-gray-800">TOPLAM</td>
                    <td className="px-6 py-3.5 text-right font-bold text-emerald-600 text-base">{fmt(summary.borc)}</td>
                    <td className="px-6 py-3.5 text-right font-bold text-teal-600">{fmt(summary.odeme)}</td>
                    <td className="px-6 py-3.5 text-right">
                      <span className={`font-bold text-base ${summary.bakiye >= 0 ? 'text-amber-600' : 'text-red-600'}`}>{fmt(summary.bakiye)}</span>
                    </td>
                    <td className="px-6 py-3.5 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        summary.borc > 0 && (summary.odeme / summary.borc) * 100 >= 80
                          ? 'bg-green-100 text-green-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {summary.borc > 0 ? `${((summary.odeme / summary.borc) * 100).toFixed(0)}%` : '—'}
                      </span>
                    </td>
                    <td className="px-6 py-3.5"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* ── MONTHLY BREAKDOWN ── */}
          <div className="bg-white rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <i className="ri-calendar-2-line text-teal-500"></i>
                Aylık Finansal Özet
              </h3>
              <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">{monthRows.length} ay</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-teal-50">
                    <th className="text-left px-6 py-3 font-semibold text-teal-800">Dönem</th>
                    <th className="text-right px-6 py-3 font-semibold text-teal-800">Kazanç</th>
                    <th className="text-right px-6 py-3 font-semibold text-teal-800">Tahsilat</th>
                    <th className="text-right px-6 py-3 font-semibold text-teal-800">Kalan</th>
                    <th className="px-6 py-3 font-semibold text-teal-800 min-w-[160px]">Kazanç Grafiği</th>
                  </tr>
                </thead>
                <tbody>
                  {monthRows.map((row, i) => {
                    const maxBorc = Math.max(...monthRows.map((r) => r.borc), 1);
                    const barWidth = (row.borc / maxBorc) * 100;
                    return (
                      <tr key={row.monthKey} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/50'}`}>
                        <td className="px-6 py-3.5 font-semibold text-gray-800 capitalize">{row.monthLabel}</td>
                        <td className="px-6 py-3.5 text-right font-bold text-emerald-600">{fmt(row.borc)}</td>
                        <td className="px-6 py-3.5 text-right text-teal-600 font-medium">{fmt(row.odeme)}</td>
                        <td className="px-6 py-3.5 text-right">
                          <span className={`font-bold ${row.bakiye > 0 ? 'text-amber-600' : row.bakiye === 0 ? 'text-gray-500' : 'text-red-500'}`}>
                            {fmt(row.bakiye)}
                          </span>
                        </td>
                        <td className="px-6 py-3.5">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-100 rounded-full h-2">
                              <div className="bg-emerald-400 h-2 rounded-full transition-all" style={{ width: `${barWidth}%` }}></div>
                            </div>
                            <span className="text-xs text-gray-400 w-8 text-right flex-shrink-0">{barWidth.toFixed(0)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
